// Global Instructions Rule Applied!
// Shape of GET /capital-calls/:id/notice-campaign/plan
// (cc__notice_campaign_plan): what "Send notices" on a capital call would
// create - per LP account its notices, its recipients on the Capital Calls
// topic and the merge fields of its email - and what blocks the send.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** An additional document attached with a notice (the ILPA workbook). */
export const NoticePlanSupplementFileSchema = z
  .object({
    file_urls_capital_call_notices__id: z.string(),
    file_urls_capital_call_notices__file_name: z.string(),
    file_urls_capital_call_notices__file_url: z.string(),
    file_urls_capital_call_notices__file_size_bytes: z.number().nullish(),
    notice_templates__code: z.string(),
    notice_templates__label: z.string().nullish(),
    notice_templates__short_label: z.string().nullish()
  })
  .strict()

export const NoticePlanNoticeSchema = z
  .object({
    capital_call_notices__id: z.string(),
    capital_call_notices__version: z.number(),
    investors__name: z.string().nullish(),
    file_urls_capital_call_notices__id: z.string(),
    file_urls_capital_call_notices__file_name: z.string(),
    file_urls_capital_call_notices__file_url: z.string(),
    file_urls_capital_call_notices__file_size_bytes: z.number().nullish(),
    prog__sent_at: z.string().nullish(),
    // attached with the notice; defaulted for older backends
    supplement_files: z.array(NoticePlanSupplementFileSchema).optional().default([])
  })
  .strict()

export const NoticePlanRecipientSchema = z
  .object({
    contacts__id: z.string(),
    contacts__first_name: z.string().nullish(),
    contacts__last_name: z.string().nullish(),
    contacts__email: z.string(),
    role: z.string()
  })
  .strict()

export const NoticePlanAccountSchema = z
  .object({
    accounts__id: z.string(),
    accounts__name: z.string().nullish(),
    prog__amount_due: z.number(),
    // formatted on the server, as the notice prints them
    merge_data: z
      .object({
        fund_name: z.string(),
        fund_legal_name: z.string(),
        call_name: z.string(),
        call_date: z.string(),
        due_date: z.string(),
        amount_due: z.string(),
        investor_names: z.string(),
        notice_count: z.number()
      })
      .strict(),
    notices: z.array(NoticePlanNoticeSchema),
    recipients: z.array(NoticePlanRecipientSchema),
    // at least one To recipient: without one the account gets no email
    prog__has_to: z.boolean()
  })
  .strict()

export const CapitalCallNoticeCampaignPlanSchema = z
  .object({
    call: z
      .object({
        capital_calls__id: z.string(),
        capital_calls__name: z.string().nullish(),
        capital_calls__call_date: z.string().nullish(),
        capital_calls__due_date: z.string().nullish()
      })
      .strict(),
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullish(),
        funds__legal_name: z.string().nullish()
      })
      .strict(),
    comms_topic_id: z.string().nullish(),
    email_design_template_id: z.string().nullish(),
    // why the notices cannot be sent, as a sentence; null when they can
    prog__blocked_reason: z.string().nullish(),
    // the system user behind the fund's notice sender email, if any
    sender: z
      .object({
        system_users__id: z.string(),
        email: z.string(),
        name: z.string().nullish(),
        // that mailbox may send campaigns
        prog__mailbox_enabled: z.boolean()
      })
      .strict()
      .nullish(),
    accounts: z.array(NoticePlanAccountSchema),
    prog__summary: z
      .object({
        accounts: z.number(),
        notices: z.number(),
        accounts_without_recipient: z.number(),
        // current notices already sent, whatever only_unsent says
        already_sent: z.number(),
        // notices of an investor with no account: they cannot be emailed
        notices_without_account: z.number(),
        // notice emails the call already has
        campaigns: z.number()
      })
      .strict()
  })
  .strict()

export type NoticePlanAccount = z.infer<typeof NoticePlanAccountSchema>
export type CapitalCallNoticeCampaignPlan = z.infer<typeof CapitalCallNoticeCampaignPlanSchema>

export function validateCapitalCallNoticeCampaignPlan(data: unknown): CapitalCallNoticeCampaignPlan {
  try {
    return validateWithZodSchema(CapitalCallNoticeCampaignPlanSchema, data, 'capital call notice campaign plan')
  } catch (error) {
    throw new Error(`Capital call notice campaign plan validation failed: ${error}`)
  }
}
